import { useState, useMemo } from 'react';
import {
	Plus,
	Search,
	Monitor,
	Crown,
	Pencil,
	Trash2,
	ArrowLeft,
	Wand2,
	Zap,
	AlertTriangle,
	Settings,
	Check,
} from 'lucide-react';
import { createPortal } from 'react-dom';
import { Button, PageTitle, ClubIcon, SearchInput } from '@/components/ui';
import { CopyWrapper } from '@/components/ui/CopyWrapper';
import { ClubConfigModal } from './ClubConfigModal';
import { MixerBenefitModal } from '../mixer/MixerBenefitModal';
import { useGetClubs } from '@/services/queries/club';
import { useRemoveClub, useRemoveClubBenefit } from '@/services/mutations/club';
import { useSearchParams } from 'react-router-dom';
import { Filter, FilterOption } from '@/components/ui/Filter';

const sortBenefitsByPriority = (benefits?: any[]) =>
	[...(benefits ?? [])].sort(
		(a, b) => (b?.display_order ?? 0) - (a?.display_order ?? 0),
	);

const filters: Array<FilterOption> = [
	{
		type: 'shelf',
		label: 'Shelf',
		options: [
			{ value: 'B2B2C', label: 'B2B2C (Empresas)' },
			{ value: 'B2C', label: 'B2C (Varejo)' },
			{ value: 'NONE', label: 'Sem shelf' },
		],
	},
	{
		type: 'price',
		label: 'Valor (preço)',
		options: [
			{ value: 'asc', label: 'Menor → Maior' },
			{ value: 'desc', label: 'Maior → Menor' },
		],
	},
];

const DeleteModal = ({
	isOpen,
	onClose,
	onConfirm,
	title,
	description,
}: any) => {
	if (!isOpen) return null;
	return createPortal(
		<div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
			<div
				className="absolute inset-0 bg-stone-900/60 backdrop-blur-[2px]"
				onClick={onClose}
			/>
			<div className="animate-in zoom-in-95 relative w-full max-w-[400px] rounded-[2rem] bg-white p-8 text-center shadow-2xl duration-200">
				<div className="mb-4 flex justify-center text-rose-500">
					<div className="rounded-full bg-rose-50 p-4">
						<AlertTriangle size={32} />
					</div>
				</div>
				<h3 className="mb-2 text-xl font-black text-stone-900">
					{title}
				</h3>
				<p
					className="mb-8 text-sm font-medium leading-relaxed text-stone-500"
					dangerouslySetInnerHTML={{ __html: description }}
				/>
				<div className="flex flex-col gap-3">
					<Button
						text="Confirmar exclusão"
						className="w-full justify-center rounded-full border-none bg-rose-600 py-3 font-bold text-white shadow-md hover:bg-rose-700"
						onClick={onConfirm}
					/>
					<button
						onClick={onClose}
						className="text-sm font-bold text-stone-400 transition-colors hover:text-stone-600"
					>
						Cancelar
					</button>
				</div>
			</div>
		</div>,
		document.body,
	);
};

const Badge = ({ shelf }: { shelf: string }) => {
	const isMain = shelf === 'MAIN' || shelf === 'B2C';
	const label =
		shelf === 'B2B2C'
			? 'B2B2C (Empresas)'
			: shelf === 'B2C' || shelf === 'MAIN'
			  ? 'B2C (Varejo)'
			  : shelf;
	return (
		<div
			className={`flex w-fit items-center gap-1.5 rounded-md border px-2 py-1 ${
				isMain
					? 'border-emerald-100 bg-emerald-50 text-emerald-600'
					: 'border-purple-100 bg-purple-50 text-purple-600'
			}`}
		>
			{isMain ? <Monitor size={14} /> : <Crown size={14} />}
			<span className="text-[10px] font-black uppercase tracking-widest">
				{label}
			</span>
		</div>
	);
};

const ClubCard = ({ club, onEdit, onDelete, onMixer }: any) => (
	<div className="group relative flex flex-col overflow-hidden rounded-2xl border border-stone-100 bg-white shadow-sm transition-all hover:shadow-md">
		<div className="flex min-h-0 flex-1 flex-col space-y-4 p-5">
			<div className="flex min-h-[2.25rem] flex-shrink-0 items-center justify-between gap-2">
				<div className="flex flex-wrap items-center gap-2">
					<Badge shelf={club.shelf ?? 'B2C'} />
					{club.render_type === 'HIGHLIGHTED' && (
						<div className="flex w-fit items-center gap-1.5 rounded-md border border-stone-300 bg-stone-100 px-2 py-1 text-stone-700">
							<Wand2 size={14} />
							<span className="text-[10px] font-black uppercase tracking-widest">
								Destaque
							</span>
						</div>
					)}
				</div>
				<div className="flex shrink-0 items-center gap-1">
					<button
						onClick={() => onEdit(club)}
						className="rounded-lg p-2 text-stone-400 hover:bg-stone-50 hover:text-stone-900"
						title="Editar"
					>
						<Pencil size={16} />
					</button>
					<button
						onClick={() => onDelete(club)}
						className="rounded-lg p-2 text-stone-400 hover:bg-rose-50 hover:text-rose-600"
						title="Excluir"
					>
						<Trash2 size={16} />
					</button>
					<button
						onClick={() => onMixer(club)}
						title="Gerenciar Benefícios"
						className="rounded-lg p-2 text-stone-400 hover:bg-indigo-50 hover:text-indigo-600"
					>
						<Settings size={16} />
					</button>
				</div>
			</div>
			<div className="flex items-center gap-3">
				<ClubIcon club={club} size="md" />
				<h3 className="line-clamp-2 h-14 text-xl font-bold leading-tight text-stone-900">
					Club: {club.name}
				</h3>
			</div>

			<p className="mt-1 line-clamp-3 h-[3.65rem] shrink-0 text-xs leading-relaxed text-stone-400">
				{club.description}
			</p>

			<ul className="h-[6.5rem] space-y-2 overflow-hidden">
				{club.benefits && club.benefits.length > 0 ? (
					<>
						{sortBenefitsByPriority(club.benefits)
							.slice(0, 3)
							.map((benefit: any) => (
								<li
									key={benefit.id}
									className="flex items-start gap-2 text-xs font-medium text-stone-600"
								>
									<Check
										size={14}
										className="mt-0.5 shrink-0 text-stone-400"
									/>
									<span className="line-clamp-1">
										{benefit.service_substitute_title ||
											benefit.service?.service_name ||
											benefit.title ||
											'Benefício'}
									</span>
								</li>
							))}
						{club.benefits.length > 3 && (
							<li className="flex items-start gap-2 text-xs font-medium text-stone-400">
								<span className="pl-[22px]">...</span>
							</li>
						)}
					</>
				) : null}
			</ul>

			<div className="border-t border-stone-50 pt-4">
				<p className="mb-0.5 text-[9px] font-black uppercase leading-none tracking-widest text-stone-600">
					Order: {club.render_order ?? '–'}
				</p>
				<p className="mb-1 text-[9px] font-black uppercase leading-none tracking-widest text-stone-600">
					Valor Mensal
				</p>
				<p className="text-lg font-black text-stone-900">
					{club.currency}{' '}
					{parseFloat(club.price).toLocaleString('pt-BR', {
						minimumFractionDigits: 2,
					})}
				</p>
			</div>
		</div>
		<div className="flex shrink-0 flex-col gap-2 border-t border-stone-100 bg-stone-50/50 px-5 py-4">
			<CopyWrapper
				valueToCopy={club.id}
				wrapperClassName="flex w-full items-center pr-8"
				buttonClassName="!right-0"
			>
				<span className="block truncate text-[10px] font-black uppercase tracking-widest text-stone-900">
					ID: {club.id}
				</span>
			</CopyWrapper>
		</div>
	</div>
);

const ClubsPage = () => {
	const { data, isLoading, isError } = useGetClubs(undefined);
	const { mutateAsync: removeClub } = useRemoveClub(() => {
		setIsDeleteModalOpen(false);
	});
	const { mutateAsync: removeBenefit } = useRemoveClubBenefit(() => {
		setIsDeleteModalOpen(false);
	});
	const [view, setView] = useState<'list' | 'mixer'>('list');
	const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
	const [isBenefitModalOpen, setIsBenefitModalOpen] = useState(false);
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
	const [deleteTarget, setDeleteTarget] = useState<'club' | 'benefit'>(
		'club',
	);
	const [selectedClub, setSelectedClub] = useState<any>(null);
	const [selectedBenefit, setSelectedBenefit] = useState<any>(null);

	const [searchParams] = useSearchParams();
	const search = searchParams.get('search') || '';
	const shelf = searchParams.get('shelf') || '';
	const priceSort = searchParams.get('price') || '';

	const clubs = Array.isArray(data) ? data : [];

	// CORREÇÃO: Deriva o clube selecionado da lista atualizada do React Query
	const currentClub = useMemo(() => {
		if (!selectedClub) return null;
		return clubs.find((c: any) => c.id === selectedClub.id) || selectedClub;
	}, [clubs, selectedClub]);

	const handleDeleteConfirm = async () => {
		if (deleteTarget === 'club' && currentClub) {
			await removeClub({ id: currentClub.id, body: currentClub });
		} else if (deleteTarget === 'benefit' && selectedBenefit) {
			await removeBenefit(selectedBenefit.id);
		} else {
			setIsDeleteModalOpen(false);
		}
	};

	const filteredClubs = useMemo(() => {
		const searchLower = search.toLowerCase();

		const list = clubs
			.filter((c: any) => c.name?.toLowerCase().includes(searchLower))
			.filter((c: any) => {
				if (!shelf) return true;
				if (shelf === 'NONE') return !c.shelf;
				return c.shelf === shelf;
			});

		const toPrice = (c: any) => {
			const n = parseFloat(String(c.price ?? '0'));
			return Number.isFinite(n) ? n : 0;
		};

		const sorted = [...list];

		if (priceSort === 'asc') {
			sorted.sort((a: any, b: any) => toPrice(a) - toPrice(b));
			return sorted;
		}
		if (priceSort === 'desc') {
			sorted.sort((a: any, b: any) => toPrice(b) - toPrice(a));
			return sorted;
		}

		sorted.sort(
			(a: any, b: any) => (b.render_order ?? 0) - (a.render_order ?? 0),
		);
		return sorted;
	}, [clubs, search, shelf, priceSort]);

	if (view === 'mixer' && currentClub) {
		return (
			<div className="animate-in fade-in slide-in-from-right-4 mx-auto max-w-7xl space-y-6 px-4 py-6 duration-300 md:space-y-8 md:p-8">
				{/* Header Superior */}
				<div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
					<div className="flex items-center gap-4 md:gap-6">
						<button
							onClick={() => setView('list')}
							className="group flex h-12 w-12 items-center justify-center rounded-full border border-stone-100 bg-white transition-all hover:border-stone-200 hover:shadow-sm"
						>
							<ArrowLeft
								size={20}
								className="text-stone-400 group-hover:text-stone-900"
							/>
						</button>
						<div className="space-y-0.5">
							<h1 className="text-2xl font-black tracking-tight text-stone-900">
								Club: {currentClub.name}
							</h1>
							<div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-widest text-stone-300">
								<span>CLUBE</span>
								<span className="text-stone-200">/</span>
								<span className="text-stone-900">
									BENEFÍCIOS VINCULADOS
								</span>
							</div>
						</div>
					</div>
					<Button
						text="Novo Vínculo"
						className="w-full rounded-full bg-black px-6 py-3 text-sm font-bold shadow-lg hover:bg-neutral-800 md:w-auto"
						startIcon={
							<Plus className="size-5 stroke-[3] text-white" />
						}
						onClick={() => {
							setSelectedBenefit(null);
							setIsBenefitModalOpen(true);
						}}
					/>
				</div>

				{/* Lista de Benefícios */}
				<div className="grid grid-cols-1 gap-4">
					{currentClub.benefits && currentClub.benefits.length > 0 ? (
						sortBenefitsByPriority(currentClub.benefits).map(
							(b: any) => (
								<div
									key={b.id}
									className="group relative flex flex-col gap-4 rounded-[2rem] border border-stone-100 bg-white p-5 transition-all hover:border-stone-200 hover:shadow-md md:flex-row md:items-center md:justify-between md:gap-6 md:p-6"
								>
									{/* Informações Principais */}
									<div className="min-w-0 flex-1 space-y-3">
										{/* Mobile: título em cima, ID à direita. Desktop: título + ID inline como antes */}
										<div className="flex flex-col gap-2 md:flex-row md:items-center md:gap-3">
											<h3 className="break-words text-lg font-black leading-snug text-stone-900 md:leading-none">
												{b.service_substitute_title ||
													b.title ||
													'Benefício sem título'}
											</h3>
											<div className="flex w-full justify-end md:contents md:w-auto">
												<CopyWrapper
													valueToCopy={b.id}
													wrapperClassName="inline-flex items-center"
													buttonClassName="!relative !right-auto !top-auto !ml-2 !translate-y-0"
												>
													<span className="rounded-full bg-stone-100 px-3 py-1 text-[9px] font-black uppercase tracking-widest text-stone-400 transition-colors">
														ID: {b.id.slice(0, 8)}
													</span>
												</CopyWrapper>
											</div>
										</div>

										{/* Badges de Detalhes - mobile: centralizado; desktop: à esquerda como antes */}
										<div className="flex w-full flex-wrap gap-4 md:justify-start">
											<div className="flex flex-col gap-1">
												<span className="text-[9px] font-black uppercase tracking-widest text-stone-300">
													Desconto
												</span>
												<div className="flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50/50 px-2 py-1 text-emerald-600">
													<Zap
														size={10}
														className="fill-emerald-600"
													/>
													<span className="text-xs font-black">
														{b.service_discount_percentage ??
															0}
														% OFF
													</span>
												</div>
											</div>

											<div className="flex flex-col gap-1">
												<span className="text-[9px] font-black uppercase tracking-widest text-stone-300">
													Quantidade
												</span>
												<div className="flex items-center gap-1.5 rounded-lg border border-stone-100 bg-stone-50 px-2 py-1 text-stone-600">
													<span className="text-xs font-black">
														{b.default_amount ?? 1}{' '}
														Unidade(s)
													</span>
												</div>
											</div>

											<div className="flex flex-col gap-1">
												<span className="text-[9px] font-black uppercase tracking-widest text-stone-300">
													Gatilho de Renovação
												</span>
												<div className="flex items-center gap-1.5 rounded-lg border border-stone-100 bg-stone-50 px-2 py-1 text-stone-600">
													<span className="text-xs font-black uppercase tracking-tight">
														{b.renewal_trigger?.replace(
															/_/g,
															' ',
														) || 'NEVER'}
													</span>
												</div>
											</div>
											<div className="flex flex-col gap-1">
												<span className="text-[9px] font-black uppercase tracking-widest text-stone-300">
													Prioridade Atual
												</span>
												<div className="flex items-center gap-1.5 rounded-lg border border-stone-100 bg-stone-50 px-2 py-1 text-stone-600">
													<span className="text-xs font-black">
														{b.display_order ?? 0}
													</span>
												</div>
											</div>
										</div>
									</div>

									{/* Ações à Direita */}
									<div className="flex w-full justify-end gap-2 pt-1 transition-all md:w-auto md:pt-0">
										{/* Ações - mobile (texto + ícone) */}
										<div className="flex gap-2 md:hidden">
											<button
												onClick={() => {
													setSelectedBenefit(b);
													setIsBenefitModalOpen(true);
												}}
												className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 transition-colors hover:bg-stone-50 hover:text-stone-900"
											>
												<Pencil size={14} />
												<span>Editar</span>
											</button>
											<button
												onClick={() => {
													setSelectedBenefit(b);
													setDeleteTarget('benefit');
													setIsDeleteModalOpen(true);
												}}
												className="flex items-center gap-1 rounded-full border border-stone-200 px-3 py-1.5 text-xs font-semibold text-stone-600 transition-colors hover:bg-rose-50 hover:text-rose-600"
											>
												<Trash2 size={14} />
												<span>Excluir</span>
											</button>
										</div>

										{/* Ações - desktop (apenas ícones) */}
										<div className="hidden gap-2 md:flex">
											<button
												onClick={() => {
													setSelectedBenefit(b);
													setIsBenefitModalOpen(true);
												}}
												className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-50 text-stone-400 transition-all hover:bg-stone-900 hover:text-white"
											>
												<Pencil size={18} />
											</button>
											<button
												onClick={() => {
													setSelectedBenefit(b);
													setDeleteTarget('benefit');
													setIsDeleteModalOpen(true);
												}}
												className="flex h-10 w-10 items-center justify-center rounded-full bg-stone-50 text-stone-400 transition-all hover:bg-rose-600 hover:text-white"
											>
												<Trash2 size={18} />
											</button>
										</div>
									</div>
								</div>
							),
						)
					) : (
						<div className="flex flex-col items-center justify-center rounded-[3rem] border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
							<div className="mb-6 rounded-full bg-white p-6 text-stone-200 shadow-sm">
								<Wand2 size={48} />
							</div>
							<h3 className="text-lg font-bold text-stone-900">
								Nenhum benefício vinculado
							</h3>
							<p className="mt-2 text-sm text-stone-400">
								Comece adicionando um novo vínculo de benefício
								a este clube.
							</p>
						</div>
					)}
				</div>

				<MixerBenefitModal
					isOpen={isBenefitModalOpen}
					onClose={() => {
						setIsBenefitModalOpen(false);
						setSelectedBenefit(null);
					}}
					clubId={currentClub.id}
					benefitToEdit={selectedBenefit}
					initialServiceIds={
						currentClub.benefits?.map(
							(b: any) => b.service_id || b.id,
						) || []
					}
				/>

				<DeleteModal
					isOpen={isDeleteModalOpen && deleteTarget === 'benefit'}
					onClose={() => setIsDeleteModalOpen(false)}
					onConfirm={handleDeleteConfirm}
					title="Remover Vínculo"
					description={`Você tem certeza que deseja remover o vínculo de <strong>${
						selectedBenefit?.service_substitute_title ||
						'este benefício'
					}</strong>?`}
				/>
			</div>
		);
	}

	if (isLoading) {
		return (
			<div className="flex h-[400px] items-center justify-center">
				<p className="animate-pulse text-sm font-black uppercase tracking-widest text-stone-400">
					Carregando clubes...
				</p>
			</div>
		);
	}

	if (isError) {
		return (
			<div className="flex h-[400px] flex-col items-center justify-center gap-4 text-center">
				<AlertTriangle size={48} className="text-rose-500" />
				<p className="text-sm font-bold text-stone-600">
					Erro ao carregar os clubes.
				</p>
			</div>
		);
	}

	return (
		<section className="animate-fade-in space-y-6 px-4 py-6">
			<div className="space-y-1">
				<PageTitle text="Clubes" />
				<p className="text-sm text-stone-400">
					Determine onde cada clube será comercializado (B2B2C vs
					B2C).
				</p>
			</div>

			<div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
				<div className="w-full sm:max-w-md">
					<SearchInput
						placeholder="Buscar clube..."
						className="bg-inherit"
					/>
				</div>
				<Button
					text="Novo Clube"
					className="w-full rounded-full bg-black px-6 py-2.5 font-bold shadow-md hover:bg-neutral-800 sm:w-auto"
					startIcon={
						<Plus className="size-5 stroke-[3] text-white" />
					}
					onClick={() => {
						setSelectedClub(null);
						setIsConfigModalOpen(true);
					}}
				/>
			</div>

			<Filter filters={filters} />

			{filteredClubs.length > 0 ? (
				<div className="grid grid-cols-1 items-start gap-6 md:grid-cols-2 lg:grid-cols-3">
					{filteredClubs.map((club: any) => (
						<ClubCard
							key={club.id}
							club={club}
							onEdit={(c: any) => {
								setSelectedClub(c);
								setIsConfigModalOpen(true);
							}}
							onDelete={(c: any) => {
								setSelectedClub(c);
								setDeleteTarget('club');
								setIsDeleteModalOpen(true);
							}}
							onMixer={(c: any) => {
								setSelectedClub(c);
								setView('mixer');
							}}
						/>
					))}
				</div>
			) : (
				<div className="flex flex-col items-center justify-center rounded-[2rem] border border-dashed border-stone-200 bg-stone-50/50 py-24 text-center">
					<div className="mb-4 rounded-full bg-white p-4 text-stone-300 shadow-sm">
						<Search size={32} />
					</div>
					<h3 className="text-base font-bold text-stone-900">
						Nenhum clube encontrado
					</h3>
					<p className="mx-auto mt-1 max-w-xs text-sm leading-relaxed text-stone-400">
						Não encontramos clubes com os critérios de busca ou
						ainda não existem clubes cadastrados.
					</p>
				</div>
			)}

			<ClubConfigModal
				isOpen={isConfigModalOpen}
				onClose={() => setIsConfigModalOpen(false)}
				club={currentClub}
			/>

			<DeleteModal
				isOpen={isDeleteModalOpen && deleteTarget === 'club'}
				onClose={() => setIsDeleteModalOpen(false)}
				onConfirm={handleDeleteConfirm}
				title="Excluir Clube"
				description={`Você tem certeza que deseja excluir o <strong>${currentClub?.name}</strong>?`}
			/>
		</section>
	);
};

export { ClubsPage };
